import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { addDays, format } from 'date-fns';
import { collection, addDoc, serverTimestamp, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X, Wallet, Calendar, TrendingUp, Target, CreditCard, ArrowRightLeft, ChevronDown } from 'lucide-react';
import { TransactionType, Goal, Debt, RecurringExpense, Transaction, BankAccount, PlannedPayment, Schedule } from '../types';

/**
 * Generate all occurrence dates for a scheduled item within the given window.
 * Returns dates sorted chronologically.
 */
function generateOccurrenceDates(
  schedule: Schedule | undefined,
  windowStart: Date,
  windowEnd: Date
): Date[] {
  if (!schedule) return [];
  const dates: Date[] = [];

  if (schedule.frequency === 'monthly' || schedule.frequency === 'semi-monthly') {
    const cur = new Date(windowStart.getFullYear(), windowStart.getMonth(), 1);
    while (cur <= windowEnd) {
      const y = cur.getFullYear();
      const m = cur.getMonth();
      if (schedule.date1) {
        const d = new Date(y, m, schedule.date1, 12, 0, 0);
        if (d >= windowStart && d <= windowEnd) dates.push(d);
      }
      if (schedule.frequency === 'semi-monthly' && schedule.date2) {
        const d = new Date(y, m, schedule.date2, 12, 0, 0);
        if (d >= windowStart && d <= windowEnd) dates.push(d);
      }
      cur.setMonth(cur.getMonth() + 1);
    }
  } else if (schedule.frequency === 'bi-weekly' || schedule.frequency === 'weekly') {
    if (schedule.nextDate) {
      const interval = schedule.frequency === 'bi-weekly' ? 14 : 7;
      let d = new Date(schedule.nextDate);
      // rewind to window start
      while (addDays(d, interval) >= windowStart) d = addDays(d, -interval);
      while (d <= windowEnd) {
        if (d >= windowStart) dates.push(new Date(d));
        d = addDays(d, interval);
      }
    }
  }

  return dates.sort((a, b) => a.getTime() - b.getTime());
}

/** Format a date as yyyy-MM-dd for storage */
const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Pick the nearest upcoming occurrence date string for a list of dates */
function defaultOccurrenceDate(dates: Date[]): string {
  if (dates.length === 0) return '';
  const now = new Date();
  const upcoming = dates.filter(d => d >= now);
  const target = upcoming.length > 0 ? upcoming[0] : dates[dates.length - 1];
  return fmtDate(target);
}

interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  householdId: string;
  goals: Goal[];
  debts: Debt[];
  bankAccounts: BankAccount[];
  recurringExpenses?: RecurringExpense[];
  plannedPayments?: PlannedPayment[];
  /** Map of plannedPayment.id -> cumulative amount paid via linked transactions */
  commitmentPaidMap?: Map<string, number>;
  onOpenGoalModal: () => void;
  onOpenDebtModal: () => void;
  onOpenBudgetModal?: () => void;
  editingTransaction?: Transaction | null;
}

export default function TransactionModal({ isOpen, onClose, userId, userEmail, householdId, goals, debts, bankAccounts, recurringExpenses = [], plannedPayments = [], commitmentPaidMap = new Map(), onOpenGoalModal, onOpenDebtModal, onOpenBudgetModal, editingTransaction }: TransactionModalProps) {
  const [type, setType] = useState<TransactionType>('spend');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [linkedId, setLinkedId] = useState('');
  const [occurrenceDate, setOccurrenceDate] = useState(''); // yyyy-MM-dd of target virtual occurrence
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'credit' | 'line_of_credit'>('cash');
  const [creditCardId, setCreditCardId] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [toBankAccountId, setToBankAccountId] = useState('');
  const [transferSourceType, setTransferSourceType] = useState<'bank' | 'loc'>('bank');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingTransaction && isOpen) {
      setType(editingTransaction.type);
      setAmount(editingTransaction.amount.toString());
      setCategory(editingTransaction.category);
      setDescription(editingTransaction.description || '');
      if (editingTransaction.date) {
        const d = editingTransaction.date;
        const pad = (n: number) => n.toString().padStart(2, '0');
        setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
      } else {
        setDate(new Date().toISOString().split('T')[0]);
      }
      setLinkedId(editingTransaction.linkedId || '');
      setOccurrenceDate(editingTransaction.occurrenceDate || '');
      setPaymentMethod(editingTransaction.paymentMethod || 'cash');
      setCreditCardId(editingTransaction.creditCardId || '');
      setBankAccountId(editingTransaction.bankAccountId || '');
      setToBankAccountId(editingTransaction.toBankAccountId || '');
      if (editingTransaction.type === 'transfer') {
        setTransferSourceType(editingTransaction.paymentMethod === 'line_of_credit' ? 'loc' : 'bank');
      } else {
        setTransferSourceType('bank');
      }
    } else if (isOpen) {
      setType('spend');
      setAmount('');
      setCategory('');
      setDescription('');
      setDate(new Date().toISOString().split('T')[0]);
      setLinkedId('');
      setOccurrenceDate('');
      setPaymentMethod('cash');
      setCreditCardId('');
      setBankAccountId('');
      setToBankAccountId('');
    }
  }, [editingTransaction, isOpen]);

  // Compute available occurrence dates for the selected recurring source
  const occurrenceOptions = useMemo<{ value: string; label: string }[]>(() => {
    const windowStart = new Date();
    windowStart.setMonth(windowStart.getMonth() - 6);
    const windowEnd = new Date();
    windowEnd.setMonth(windowEnd.getMonth() + 6);

    let schedule: import('../types').Schedule | undefined;
    if (type === 'expense' && linkedId) {
      schedule = recurringExpenses.find(e => e.id === linkedId)?.schedule;
    } else if (type === 'debt_payment' && linkedId) {
      schedule = debts.find(d => d.id === linkedId)?.schedule;
    }

    const dates = generateOccurrenceDates(schedule, windowStart, windowEnd);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return dates.map(d => {
      const key = fmtDate(d);
      const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000);
      let suffix = '';
      if (diffDays < 0) {
        const monthsAgo = Math.abs(Math.round(diffDays / 30));
        suffix = monthsAgo <= 1 ? '← Last month' : `← ${monthsAgo} months ago`;
      } else if (diffDays <= 7) {
        suffix = '← This period';
      } else {
        const monthsAhead = Math.round(diffDays / 30);
        suffix = monthsAhead <= 1 ? '→ Next period' : `→ ${monthsAhead} months ahead`;
      }
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return { value: key, label: `${label}  ${suffix}` };
    });
  }, [type, linkedId, recurringExpenses, debts]);

  if (!isOpen) return null;

  const handleTypeChange = (val: TransactionType) => {
    setType(val);
    setLinkedId('');
    setOccurrenceDate('');
  };

  const handleLinkedChange = (val: string) => {
    if (val === 'NEW_GOAL') {
      onOpenGoalModal();
      return;
    }
    if (val === 'NEW_DEBT') {
      onOpenDebtModal();
      return;
    }
    if (val === 'NEW_EXPENSE' && onOpenBudgetModal) {
      onOpenBudgetModal();
      return;
    }
    
    setLinkedId(val);

    // Auto-pick the nearest upcoming occurrence when a recurring source is selected
    const windowStart = new Date();
    windowStart.setMonth(windowStart.getMonth() - 6);
    const windowEnd = new Date();
    windowEnd.setMonth(windowEnd.getMonth() + 6);

    if (type === 'debt_payment') {
      const debt = debts.find(d => d.id === val);
      if (debt) {
        setAmount(debt.monthlyMinimum.toString());
        setCategory(debt.name);
        const dates = generateOccurrenceDates(debt.schedule, windowStart, windowEnd);
        setOccurrenceDate(defaultOccurrenceDate(dates));
      }
    } else if (type === 'expense') {
      const exp = recurringExpenses.find(e => e.id === val);
      if (exp) {
        setAmount(exp.amount.toString());
        setCategory(exp.category);
        const dates = generateOccurrenceDates(exp.schedule, windowStart, windowEnd);
        setOccurrenceDate(defaultOccurrenceDate(dates));
      }
    } else if (type === 'savings_contribution') {
      const goal = goals.find(g => g.id === val);
      if (goal) {
        setCategory(goal.name);
        setOccurrenceDate('');
      }
    } else if (type === 'spend' && val.startsWith('planned_')) {
      const ppId = val.replace('planned_', '');
      const pp = plannedPayments.find(p => p.id === ppId);
      if (pp) {
        setAmount(pp.amount.toString());
        setCategory(pp.commitmentName);
        setOccurrenceDate('');
      }
    } else {
      setOccurrenceDate('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || isNaN(Number(amount))) return;
    
    setLoading(true);
    try {
      const numAmount = Number(amount);
      const [year, month, day] = date.split('-');
      const txDate = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0); // Noon to avoid timezone shift

      const finalPaymentMethod = (type === 'spend' || type === 'expense' || type === 'debt_payment') 
        ? paymentMethod 
        : (type === 'transfer' && transferSourceType === 'loc' ? 'line_of_credit' : 'cash');
      const finalCreditCardId = (finalPaymentMethod === 'credit' || finalPaymentMethod === 'line_of_credit') ? (creditCardId || null) : null;
      const finalBankAccountId = type === 'transfer' 
        ? (transferSourceType === 'bank' ? (bankAccountId || null) : null) 
        : ((finalPaymentMethod === 'cash') ? (bankAccountId || null) : null);
      const finalToBankAccountId = type === 'transfer' ? (toBankAccountId || null) : null;
      const finalCategory = type === 'transfer' ? 'Transfer' : category;

      if (editingTransaction) {
        if (editingTransaction.linkedId) {
          if (editingTransaction.type === 'savings_contribution') {
            await updateDoc(doc(db, 'goals', editingTransaction.linkedId), {
              currentAmount: increment(-editingTransaction.amount)
            });
          } else if (editingTransaction.type === 'debt_payment') {
            await updateDoc(doc(db, 'debts', editingTransaction.linkedId), {
              currentAmount: increment(editingTransaction.amount)
            });
          }
        }

        if ((editingTransaction.paymentMethod === 'credit' || editingTransaction.paymentMethod === 'line_of_credit') && editingTransaction.creditCardId) {
          await updateDoc(doc(db, 'debts', editingTransaction.creditCardId), {
            currentAmount: increment(-editingTransaction.amount)
          });
        }
        
        if (editingTransaction.bankAccountId) {
          if (editingTransaction.type === 'transfer') {
            await updateDoc(doc(db, 'bank_accounts', editingTransaction.bankAccountId), {
              currentBalance: increment(editingTransaction.amount)
            });
          } else {
            const isIncome = editingTransaction.type === 'income';
            await updateDoc(doc(db, 'bank_accounts', editingTransaction.bankAccountId), {
              currentBalance: increment(isIncome ? -editingTransaction.amount : editingTransaction.amount)
            });
          }
        }

        if (editingTransaction.type === 'transfer' && editingTransaction.toBankAccountId) {
           await updateDoc(doc(db, 'bank_accounts', editingTransaction.toBankAccountId), {
             currentBalance: increment(-editingTransaction.amount)
           });
        }

        if (linkedId) {
          if (type === 'savings_contribution') {
            await updateDoc(doc(db, 'goals', linkedId), {
              currentAmount: increment(numAmount)
            });
          } else if (type === 'debt_payment') {
            await updateDoc(doc(db, 'debts', linkedId), {
              currentAmount: increment(-numAmount)
            });
          }
        }

        if ((finalPaymentMethod === 'credit' || finalPaymentMethod === 'line_of_credit') && finalCreditCardId) {
          await updateDoc(doc(db, 'debts', finalCreditCardId), {
            currentAmount: increment(numAmount)
          });
        }

        if (finalBankAccountId) {
          if (type === 'transfer') {
            await updateDoc(doc(db, 'bank_accounts', finalBankAccountId), {
              currentBalance: increment(-numAmount)
            });
          } else {
            const isIncome = type === 'income';
            await updateDoc(doc(db, 'bank_accounts', finalBankAccountId), {
              currentBalance: increment(isIncome ? numAmount : -numAmount)
            });
          }
        }

        if (type === 'transfer' && finalToBankAccountId) {
           await updateDoc(doc(db, 'bank_accounts', finalToBankAccountId), {
             currentBalance: increment(numAmount)
           });
        }

        if (editingTransaction.linkedId !== linkedId) {
           if (editingTransaction.linkedId?.startsWith('planned_')) {
              const oldPpId = editingTransaction.linkedId.replace('planned_', '');
              const oldPp = plannedPayments.find(p => p.id === oldPpId);
              try {
                // Reverse this transaction's own amount out of the old
                // payment's paidAmount too, not just its status/link —
                // otherwise re-linking a transaction to a different
                // installment leaves the old one showing a stale "already
                // paid" balance it never actually received (found via a
                // real report: relinking to the correct installment left
                // the wrong one stuck at its old paidAmount with status
                // reverted to 'pending', so it displayed as fully paid
                // everywhere that reads paidAmount directly while still
                // counting as outstanding by status).
                if (oldPp) {
                  const revertedPaidAmount = Math.max(0, (oldPp.paidAmount || 0) - editingTransaction.amount);
                  await updateDoc(doc(db, 'planned_payments', oldPpId), {
                    paidAmount: revertedPaidAmount,
                    status: revertedPaidAmount >= oldPp.amount ? 'paid' : 'pending',
                    linkedTransactionId: null
                  });
                } else {
                  await updateDoc(doc(db, 'planned_payments', oldPpId), { status: 'pending', linkedTransactionId: null });
                }
              } catch (e) {
                console.error("Failed to revert old planned payment", e);
              }
           }
        }

        await updateDoc(doc(db, 'transactions', editingTransaction.id), {
          type,
          amount: numAmount,
          category: finalCategory,
          description,
          linkedId: linkedId || null,
          occurrenceDate: (type === 'expense' || type === 'debt_payment') && occurrenceDate ? occurrenceDate : null,
          date: txDate,
          paymentMethod: finalPaymentMethod,
          creditCardId: finalCreditCardId,
          bankAccountId: finalBankAccountId,
          toBankAccountId: finalToBankAccountId
        });

        if (linkedId && linkedId.startsWith('planned_')) {
          const ppId = linkedId.replace('planned_', '');
          const pp = plannedPayments.find(p => p.id === ppId);
          if (pp) {
            // Reverse the old linked tx amount from paidAmount
            const oldLinkedAmount = editingTransaction.linkedId?.startsWith('planned_') && editingTransaction.linkedId === linkedId
              ? editingTransaction.amount
              : 0;
            const newPaidAmount = Math.max(0, (pp.paidAmount || 0) - oldLinkedAmount + numAmount);
            const isFullyPaid = newPaidAmount >= pp.amount;
            try {
              await updateDoc(doc(db, 'planned_payments', ppId), {
                paidAmount: newPaidAmount,
                status: isFullyPaid ? 'paid' : 'pending',
                manuallyCompleted: isFullyPaid ? (pp.manuallyCompleted || false) : false,
                linkedTransactionId: editingTransaction.id
              });
            } catch (e) {
              console.error("Failed to update planned payment", e);
            }
          }
        }

      } else {
        const docRef = await addDoc(collection(db, 'transactions'), {
          userId,
          userEmail,
          householdId,
          type,
          amount: numAmount,
          category: finalCategory,
          description,
          linkedId: linkedId || null,
          occurrenceDate: (type === 'expense' || type === 'debt_payment') && occurrenceDate ? occurrenceDate : null,
          date: txDate,
          paymentMethod: finalPaymentMethod,
          creditCardId: finalCreditCardId,
          bankAccountId: finalBankAccountId,
          toBankAccountId: finalToBankAccountId
        });

        if (linkedId && linkedId.startsWith('planned_')) {
          const ppId = linkedId.replace('planned_', '');
          const pp = plannedPayments.find(p => p.id === ppId);
          if (pp) {
            const newPaidAmount = (pp.paidAmount || 0) + numAmount;
            const isFullyPaid = newPaidAmount >= pp.amount;
            try {
              await updateDoc(doc(db, 'planned_payments', ppId), {
                paidAmount: newPaidAmount,
                status: isFullyPaid ? 'paid' : 'pending',
                linkedTransactionId: docRef.id
              });
            } catch (e) {
              console.error("Failed to update planned payment", e);
            }
          }
        }

        if (linkedId) {
          if (type === 'savings_contribution') {
            await updateDoc(doc(db, 'goals', linkedId), {
              currentAmount: increment(numAmount)
            });
          } else if (type === 'debt_payment') {
            await updateDoc(doc(db, 'debts', linkedId), {
              currentAmount: increment(-numAmount)
            });
          }
        }

        if ((finalPaymentMethod === 'credit' || finalPaymentMethod === 'line_of_credit') && finalCreditCardId) {
          await updateDoc(doc(db, 'debts', finalCreditCardId), {
            currentAmount: increment(numAmount)
          });
        }

        if (finalBankAccountId) {
          if (type === 'transfer') {
            await updateDoc(doc(db, 'bank_accounts', finalBankAccountId), {
              currentBalance: increment(-numAmount)
            });
          } else {
            const isIncome = type === 'income';
            await updateDoc(doc(db, 'bank_accounts', finalBankAccountId), {
              currentBalance: increment(isIncome ? numAmount : -numAmount)
            });
          }
        }

        if (type === 'transfer' && finalToBankAccountId) {
           await updateDoc(doc(db, 'bank_accounts', finalToBankAccountId), {
             currentBalance: increment(numAmount)
           });
        }
      }

      onClose();
      setAmount('');
      setCategory('');
      setDescription('');
      setLinkedId('');
      setBankAccountId('');
      setToBankAccountId('');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h3 className="text-xl font-bold text-slate-900">
            {editingTransaction?.id ? 'Edit Transaction' : 'Record Transaction'}
          </h3>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!editingTransaction?.id && linkedId && (
            <div className="p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
              <strong>Note:</strong> This will create a new manual transaction and withdraw the amount from the selected account. If this payment was already synced from your bank, cancel this and edit the existing bank transaction instead.
            </div>
          )}

          <div className="mb-2">
            <label className="block text-sm font-semibold text-slate-700 mb-3">What kind of transaction is this?</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => handleTypeChange('spend')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border ${type === 'spend' ? 'bg-blue-50 border-blue-500 text-blue-700 ring-1 ring-blue-500' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'} transition-all`}
              >
                <Wallet className="w-6 h-6 mb-2" />
                <span className="text-xs font-semibold text-center">Ad-Hoc Spend</span>
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('expense')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border ${type === 'expense' ? 'bg-amber-50 border-amber-500 text-amber-700 ring-1 ring-amber-500' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'} transition-all`}
              >
                <Calendar className="w-6 h-6 mb-2" />
                <span className="text-xs font-semibold text-center">Recurring Expense</span>
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('debt_payment')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border ${type === 'debt_payment' ? 'bg-rose-50 border-rose-500 text-rose-700 ring-1 ring-rose-500' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'} transition-all`}
              >
                <CreditCard className="w-6 h-6 mb-2" />
                <span className="text-xs font-semibold text-center">Debt Payment</span>
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('income')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border ${type === 'income' ? 'bg-emerald-50 border-emerald-500 text-emerald-700 ring-1 ring-emerald-500' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'} transition-all`}
              >
                <TrendingUp className="w-6 h-6 mb-2" />
                <span className="text-xs font-semibold text-center">Income</span>
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('savings_contribution')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border ${type === 'savings_contribution' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 ring-1 ring-indigo-500' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'} transition-all`}
              >
                <Target className="w-6 h-6 mb-2" />
                <span className="text-xs font-semibold text-center">Savings</span>
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('transfer')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border ${type === 'transfer' ? 'bg-purple-50 border-purple-500 text-purple-700 ring-1 ring-purple-500' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'} transition-all`}
              >
                <ArrowRightLeft className="w-6 h-6 mb-2" />
                <span className="text-xs font-semibold text-center">Transfer</span>
              </button>
            </div>
          </div>

          {(type === 'spend' || type === 'expense' || type === 'debt_payment') && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Method</label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input 
                    type="radio" 
                    name="paymentMethod" 
                    value="cash" 
                    checked={paymentMethod === 'cash'} 
                    onChange={() => setPaymentMethod('cash')} 
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  Cash / Checking
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input 
                    type="radio" 
                    name="paymentMethod" 
                    value="credit" 
                    checked={paymentMethod === 'credit'} 
                    onChange={() => setPaymentMethod('credit')} 
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  Credit Card
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input 
                    type="radio" 
                    name="paymentMethod" 
                    value="line_of_credit" 
                    checked={paymentMethod === 'line_of_credit'} 
                    onChange={() => setPaymentMethod('line_of_credit')} 
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  Line of Credit
                </label>
              </div>
            </div>
          )}

          {(paymentMethod === 'credit' || paymentMethod === 'line_of_credit') && (type === 'spend' || type === 'expense' || type === 'debt_payment') && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Select {paymentMethod === 'line_of_credit' ? 'Line of Credit' : 'Credit Card'}</label>
              <select
                value={creditCardId}
                onChange={(e) => setCreditCardId(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                required={(paymentMethod === 'credit' || paymentMethod === 'line_of_credit') && (type === 'spend' || type === 'expense' || type === 'debt_payment')}
              >
                <option value="">Choose a {paymentMethod === 'line_of_credit' ? 'LOC' : 'card'}...</option>
                {debts.filter(d => paymentMethod === 'line_of_credit' ? d.type === 'line_of_credit' : d.type === 'credit_card').map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}

          {((paymentMethod === 'cash' && (type === 'spend' || type === 'expense' || type === 'debt_payment')) || type === 'income' || type === 'savings_contribution') && bankAccounts.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Select Bank Account</label>
              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">No specific account</option>
                {bankAccounts.map(b => (
                  <option key={b.id} value={b.id}>{b.name} (${b.currentBalance?.toFixed(2) || '0.00'})</option>
                ))}
              </select>
            </div>
          )}

          {type === 'transfer' && (bankAccounts.length > 0 || debts.some(d => d.type === 'line_of_credit')) && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Transfer Source Type</label>
                <div className="flex gap-6 mb-4">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="radio" checked={transferSourceType === 'bank'} onChange={() => setTransferSourceType('bank')} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                    Bank Account
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="radio" checked={transferSourceType === 'loc'} onChange={() => setTransferSourceType('loc')} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                    Line of Credit
                  </label>
                </div>

                {transferSourceType === 'bank' ? (
                  <>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">From Account</label>
                    <select
                      value={bankAccountId}
                      onChange={(e) => setBankAccountId(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      required
                    >
                      <option value="">Select source account</option>
                      {bankAccounts.map(b => (
                        <option key={b.id} value={b.id}>{b.name} (${b.currentBalance?.toFixed(2) || '0.00'})</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">From Line of Credit</label>
                    <select
                      value={creditCardId}
                      onChange={(e) => setCreditCardId(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      required
                    >
                      <option value="">Select source LOC</option>
                      {debts.filter(d => d.type === 'line_of_credit').map(d => (
                        <option key={d.id} value={d.id}>{d.name} (${d.currentAmount?.toFixed(2) || '0.00'})</option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">To Account</label>
                <select
                  value={toBankAccountId}
                  onChange={(e) => setToBankAccountId(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  <option value="">Select destination account</option>
                  {bankAccounts.map(b => (
                    <option key={b.id} value={b.id}>{b.name} (${b.currentBalance?.toFixed(2) || '0.00'})</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {type === 'savings_contribution' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Link to Goal</label>
              <select
                value={linkedId}
                onChange={(e) => handleLinkedChange(e.target.value)}
                className="w-full px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              >
                <option value="">No Goal Attached</option>
                {goals.map(g => (
                  <option key={g.id} value={g.id}>{g.name} (Goal: ${g.targetAmount})</option>
                ))}
                <option value="NEW_GOAL" className="font-bold text-indigo-600">+ Set New Goal</option>
              </select>
            </div>
          )}

          {type === 'spend' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Link to Planned Commitment (Optional)</label>
              <select
                value={linkedId.startsWith('planned_') ? linkedId : ''}
                onChange={(e) => handleLinkedChange(e.target.value)}
                className="w-full px-4 py-2 bg-blue-50 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">No Commitment Attached</option>
                {plannedPayments
                  .filter(p => {
                    // Show if not manually completed AND not fully paid via transactions
                    if (p.manuallyCompleted) return false;
                    const alreadyPaid = commitmentPaidMap.get(p.id!) || 0;
                    const isFullyPaid = alreadyPaid >= p.amount;
                    // Always show the currently-linked one (for editing)
                    const isCurrentlyLinked = p.id === linkedId?.replace('planned_', '');
                    return !isFullyPaid || isCurrentlyLinked;
                  })
                  .map(p => {
                    const alreadyPaid = commitmentPaidMap.get(p.id!) || 0;
                    const remaining = Math.max(0, p.amount - alreadyPaid);
                    // Include the row's own description + date, not just the
                    // commitment name + amount — two installments of the same
                    // plan are frequently the same amount (e.g. two $150
                    // payments), which made them render as literally
                    // identical, indistinguishable option text. That's what
                    // caused the wrong installment to get picked and marked
                    // paid instead of the intended one.
                    const dateLabel = format(p.date, 'MMM d, yyyy');
                    const detail = p.description ? `${p.description} — ${dateLabel}` : dateLabel;
                    const label = alreadyPaid > 0
                      ? `${p.commitmentName}: ${detail} ($${remaining.toFixed(2)} remaining of $${p.amount.toFixed(2)})`
                      : `${p.commitmentName}: ${detail} ($${p.amount.toFixed(2)})`;
                    return (
                      <option key={p.id} value={`planned_${p.id}`}>{label}</option>
                    );
                  })
                }
              </select>
            </div>
          )}

          {type === 'debt_payment' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Link to Debt</label>
              <select
                value={linkedId}
                onChange={(e) => handleLinkedChange(e.target.value)}
                className="w-full px-4 py-2 bg-rose-50 border border-rose-200 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
              >
                <option value="">Select a Debt</option>
                {debts.map(d => (
                  <option key={d.id} value={d.id}>{d.name} (Balance: ${d.currentAmount})</option>
                ))}
                <option value="NEW_DEBT" className="font-bold text-rose-600">+ Add New Debt</option>
              </select>
              {/* Period picker for debt payments */}
              {linkedId && occurrenceOptions.length > 0 && (
                <div className="mt-2">
                  <label className="block text-xs font-semibold text-rose-700 mb-1 uppercase tracking-wider">Which billing period is this for?</label>
                  <select
                    value={occurrenceDate}
                    onChange={(e) => setOccurrenceDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-rose-200 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm"
                  >
                    <option value="">Auto-detect (nearest upcoming)</option>
                    {occurrenceOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {type === 'expense' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Link to Fixed Expense</label>
              <select
                value={linkedId}
                onChange={(e) => handleLinkedChange(e.target.value)}
                className="w-full px-4 py-2 bg-amber-50 border border-amber-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              >
                <option value="">Select an Expense</option>
                {recurringExpenses.map(e => (
                  <option key={e.id} value={e.id}>{e.name || e.category} (${e.amount})</option>
                ))}
                <option value="NEW_EXPENSE" className="font-bold text-amber-600">+ Add New Fixed Expense</option>
              </select>
              {/* Period picker for recurring expense payments */}
              {linkedId && occurrenceOptions.length > 0 && (
                <div className="mt-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
                  <label className="block text-xs font-bold text-amber-700 mb-1.5 uppercase tracking-wider flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Which billing period is this payment for?
                  </label>
                  <select
                    value={occurrenceDate}
                    onChange={(e) => setOccurrenceDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-amber-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
                  >
                    <option value="">Auto-detect (nearest upcoming)</option>
                    {occurrenceOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {occurrenceDate && (
                    <p className="text-[10px] text-amber-600 mt-1 font-medium">
                      This transaction will be recorded against the {new Date(occurrenceDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} occurrence.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="0.00"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="e.g. Groceries, Salary, Rent"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Description (Optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Brief details..."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-blue-600 text-white rounded-md font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 mt-4 shadow-sm transition-colors"
          >
            {loading ? (editingTransaction ? 'Saving...' : 'Adding...') : (editingTransaction ? 'Save Changes' : 'Add Transaction')}
          </button>
        </form>
      </div>
    </div>
  );
}
